import imageUrlBuilder from '@sanity/image-url';
import { createServerFn } from '@tanstack/react-start';
import { defineQuery } from 'groq';

import type {
  PostQueryResult,
  PostsQueryResult,
  SitemapPostsQueryResult,
} from '@/sanity.types';

import { createSanityClient, sanityDataset, sanityProjectId } from './client';

export type BlogPostSummary = PostsQueryResult[number];
export type BlogPost = NonNullable<PostQueryResult>;
export type SanityImage = NonNullable<BlogPostSummary['coverImage']>;

const postsQuery = defineQuery(`*[
  _type == "post" &&
  defined(slug.current) &&
  defined(publishedAt) &&
  publishedAt <= now()
] | order(publishedAt desc) {
  "id": _id,
  title,
  "slug": slug.current,
  excerpt,
  publishedAt,
  "coverImage": coverImage {
    _type,
    asset,
    alt,
    crop,
    hotspot
  },
  "author": {
    "name": author->name,
    "role": coalesce(author->role, null)
  }
}`);

const postQuery = defineQuery(`*[
  _type == "post" &&
  slug.current == $slug &&
  defined(publishedAt) &&
  publishedAt <= now()
][0] {
  "id": _id,
  "updatedAt": _updatedAt,
  title,
  "slug": slug.current,
  excerpt,
  publishedAt,
  "coverImage": coverImage {
    _type,
    asset,
    alt,
    crop,
    hotspot
  },
  "body": body[] {
    _key,
    _type,
    _type == "block" => {
      children[] {
        _key,
        _type,
        marks,
        text
      },
      markDefs[] {
        _key,
        _type,
        href
      },
      style,
      listItem,
      level
    },
    _type == "image" => {
      asset,
      alt,
      crop,
      hotspot
    },
    _type == "cta" => {
      heading,
      "text": coalesce(text, null),
      buttonLabel,
      url
    },
    _type == "waitlist" => {
      "heading": coalesce(heading, null),
      "text": coalesce(text, null)
    },
    _type == "agentWaitlist" => {
      "heading": coalesce(heading, null),
      "text": coalesce(text, null)
    },
    _type == "regionDeadlines" => {
      heading,
      badge,
      summaryLines,
      selectLabel,
      regions[] {
        _key,
        name,
        opensOn,
        closesOn
      },
      reminderLink {
        upcomingLabel,
        openLabel,
        url
      },
      footnote
    },
    _type == "compareWidget" => {
      "heading": coalesce(heading, null),
      "text": coalesce(text, null)
    },
    _type == "compareSection" => {
      "heading": coalesce(heading, null),
      "text": coalesce(text, null)
    }
  },
  "faqs": coalesce(faqs[] {
    question,
    answer
  }, []),
  "seoTitle": coalesce(seoTitle, null),
  "seoDescription": coalesce(seoDescription, null),
  "author": {
    "name": author->name,
    "role": coalesce(author->role, null)
  }
}`);

const sitemapPostsQuery = defineQuery(`*[
  _type == "post" &&
  defined(slug.current) &&
  defined(publishedAt) &&
  publishedAt <= now()
] | order(publishedAt desc) {
  "slug": slug.current,
  "publishedAt": publishedAt,
  "updatedAt": _updatedAt
}`);

/** Public server function: it only reads published documents from Sanity. */
export const getBlogPosts = createServerFn({ method: 'GET' }).handler(
  async (): Promise<BlogPostSummary[]> => {
    const client = createSanityClient();

    try {
      return await client.fetch(postsQuery);
    } catch (error) {
      console.error('Failed to load Sanity blog posts', error);
      return [];
    }
  },
);

/** Public server function: it only reads one published document from Sanity. */
export const getBlogPost = createServerFn({ method: 'GET' })
  .validator((slug: unknown) => {
    if (typeof slug !== 'string') {
      throw new Error('Blog post slug must be a string');
    }

    const normalizedSlug = slug.trim();
    if (normalizedSlug.length === 0 || normalizedSlug.length > 96) {
      throw new Error('Blog post slug must be between 1 and 96 characters');
    }

    return normalizedSlug;
  })
  .handler(async ({ data: slug }): Promise<BlogPost | null> => {
    const client = createSanityClient();

    try {
      return await client.fetch(postQuery, { slug });
    } catch (error) {
      console.error('Failed to load Sanity blog post', error);
      return null;
    }
  });

export async function getSitemapPosts(): Promise<SitemapPostsQueryResult> {
  const client = createSanityClient();

  try {
    return await client.fetch(sitemapPostsQuery);
  } catch (error) {
    console.error('Failed to load Sanity posts for the sitemap', error);
    return [];
  }
}

export function getSanityImageUrl(image: SanityImage, width: number): string {
  return imageUrlBuilder({
    projectId: sanityProjectId,
    dataset: sanityDataset,
  })
    .image(image)
    .width(width)
    .auto('format')
    .url();
}

export function getSanityImageSources(
  image: SanityImage,
  widths: readonly number[],
) {
  const sortedWidths = [...widths].sort((left, right) => left - right);
  const largestWidth = sortedWidths[sortedWidths.length - 1];
  if (largestWidth == null) {
    throw new Error('Sanity image sources need at least one width');
  }

  return {
    src: getSanityImageUrl(image, largestWidth),
    srcSet: sortedWidths
      .map((width) => `${getSanityImageUrl(image, width)} ${width}w`)
      .join(', '),
  };
}

export function getSanitySocialImageUrl(image: SanityImage): string {
  return imageUrlBuilder({
    projectId: sanityProjectId,
    dataset: sanityDataset,
  })
    .image(image)
    .width(1200)
    .height(630)
    .fit('crop')
    .format('jpg')
    .url();
}
